<template>
  <NotFound v-if="isNotFound" />

  <Status v-else-if="status !== 'ready'" :type="status" />

  <div v-else-if="competitionData" class="page container">
    <Hero :title="heroTitle" :subtitle="heroSubtitle" :cover="competition.banerUrl" :display="isDisplay" />

    <Partners :partners="partners" :display="isDisplay" />

    <Message
      :time="recentMessage.settedTime"
      :content="recentMessage.message"
      :clickable="competitionData.message.length > 0"
      @open-messages-modal="$emit('modal-status', 'messages')"
    />

    <MessagesModal
      v-if="modal === 'messages'"
      :messages="competitionData.message"
      @close-modal="$emit('modal-status', false)"
    />

    <PenaltiesModal
      v-if="modal === 'penalties'"
      :penalties="allPenalties"
      :current-championship="currentChampionship"
      @close-modal="$emit('modal-status', false)"
    />

    <div class="competition-nav">
      <ul class="championships">
        <li v-for="championship in competitionData.championship" :key="championship.championshipId">
          <button
            :class="{ active: currentChampionship === championship }"
            :title="championship.fullName"
            @click="onChampionshipClick(championship)"
          >
            {{ championship.shortName }}
          </button>
        </li>
      </ul>

      <ul v-if="championshipStages.length" class="stages">
        <li v-for="stage in championshipStages" :key="stage.stageId">
          <button
            :class="{
              active: currentStage === stage,
            }"
            @click="currentStage = stage"
          >
            <div class="name" v-html="stage.nameI18n ? $t('stage-' + stage.nameI18n) : stage.name"></div>
            <div v-if="stage.settedTime" class="meta">{{ $t("startTimeAt") + " " + stage.settedTime }}</div>
          </button>
        </li>
      </ul>
    </div>

    <div class="content">
      <Results
        :competition-data="competitionData"
        :results="stageResults"
        :result-precision="competition.precision"
        :filters="stageFilters"
        :current-stage="currentStage"
        :current-championship="currentChampionship"
        :after-stage="afterStage"
        :last-time="lastTime"
        @switch-penalties="afterStage = !afterStage"
      />
    </div>

    <LiveFrame v-if="!isDisplay" :is-online="Boolean(competition.liveRelation)" :live-url="competition.liveRelation" />

    <LostConnectionModal
      v-if="
        modal === 'lostConnectionModal' &&
        !this.competitionData.archival &&
        this.competitionData.competition[0].mode === 0
      "
      @close-modal="$emit('modal-status', false)"
    />

    <transition name="inactivity-fade">
      <div v-if="isDisplay && showInactivityModal" class="inactivity-overlay">
        <div class="inactivity-box">
          <h2 class="inactivity-box__title">{{ $t("inactivityTitle") }}</h2>
          <div class="inactivity-box__countdown">{{ countdown }}</div>
          <p class="inactivity-box__subtitle">{{ $tc("inactivityCountdown", countdown, { n: countdown }) }}</p>
          <div class="inactivity-box__progress">
            <div class="inactivity-box__progress-bar" :style="{ width: (countdown / 15) * 100 + '%' }"></div>
          </div>
          <p class="inactivity-box__hint">{{ $t("inactivityHint") }}</p>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import NotFound from "@/views/NotFound.view"
import Hero from "@/components/Hero"
import Partners from "@/components/Partners"
import Message from "@/components/Message"
import Results from "@/components/Results"
import Status from "@/components/Status"
import LiveFrame from "@/components/LiveFrame"
import MessagesModal from "@/components/MessagesModal"
import PenaltiesModal from "@/components/PenaltiesModal"
import LostConnectionModal from "@/components/LostConnectionModal"
import { config } from "@/config"
import { hasSavedLocale } from "@/locale"

export default {
  name: "Competition",

  props: {
    modal: [String, Boolean],
  },

  components: {
    Hero,
    Partners,
    Message,
    Results,
    LiveFrame,
    Status,
    NotFound,
    MessagesModal,
    PenaltiesModal,
    LostConnectionModal,
  },

  data: () => ({
    isUpdating: false,
    isNotFound: false,
    status: "loading",
    lastTime: null,
    lastTimeTimer: null,

    partners: null,
    competitionData: null,

    currentChampionship: null,
    currentStage: null,
    currentHash: null,
    ws: null,
    afterStage: false,

    displayConfig: null,
    showInactivityModal: false,
    countdown: 15,
    inactivityTimer: null,
    countdownTimer: null,
    configRetryTimer: null,
  }),

  computed: {
    isDisplay: function () {
      return this.$route.meta.display === true
    },

    recentMessage: function () {
      if (this.competitionData.message.length === 0) return ""
      return this.competitionData.message.reduce((prev, current) =>
        prev.messageId > current.messageId ? prev : current
      )
    },

    competition: function () {
      return this.competitionData.competition[0]
    },

    championshipStages: function () {
      return this.competitionData.stage.filter((stage) => {
        return stage.championshipIds && stage.championshipIds.includes(this.currentChampionship.id)
      })
    },

    championshipCompetitors: function () {
      return this.competitionData.competitor.filter(
        (competitor) => competitor.championshipParticipation[this.currentChampionship.id]
      )
    },

    stageResults: function () {
      const results = []
      const length = this.getStageLength()
      if (length === null) return []

      this.championshipCompetitors.forEach((competitor) => {
        let result = this.getCompetitorTime(competitor.id)
        let previousResult = null
        if (this.afterStage && this.currentStage.stageNo > 1)
          previousResult = this.getCompetitorTimeAfterStage(
            competitor.id,
            this.currentStage.stageNo - 1,
            this.currentStage.type
          )
        const penalties = this.competition.penalties
          ? this.getCompetitorPenalties(competitor.id, this.currentStage)
          : { in: 0, out: 0 }
        const penalty = this.competition.penalties ? this.getEffectivePenalty(penalties, this.currentStage) : 0
        const previousStage =
          this.currentStage.stageNo > 1
            ? this.competitionData.stage.find((stage) => stage.stageNo === this.currentStage.stageNo - 1)
            : null
        const previousPenalty =
          this.competition.penalties && previousStage && this.afterStage
            ? this.getEffectivePenalty(this.getCompetitorPenalties(competitor.id, previousStage), previousStage)
            : 0
        const splitTimes = this.getCompetitorSplitTimes(competitor.id, this.currentStage)
        const avgSpeed = this.getAvgSpeed(result, length)

        if (result) {
          results.push({
            ...competitor,
            result,
            previousResult,
            avgSpeed,
            splitTimes,
            penalty,
            penaltyIn: penalties ? penalties.in : 0,
            penaltyOut: penalties ? penalties.out : 0,
            previousPenalty,
            realResult: result,
          })
        }
      })

      return results.sort((a, b) => {
        if ([0, 1, 6, 7].includes(this.currentStage.type)) {
          if (isNaN(a.result) && !isNaN(b.result)) return 1
          if (!isNaN(a.result) && isNaN(b.result)) return -1
          return this.competition.penalties ? a.result + a.penalty - (b.result + b.penalty) : a.result - b.result
        } else if ([2, 3].includes(this.currentStage.type)) {
          if (isNaN(a.result.time) && !isNaN(b.result.time)) return 1
          if (!isNaN(a.result.time) && isNaN(b.result.time)) return -1
          return a.result.time - b.result.time
        } else if ([4, 5].includes(this.currentStage.type)) {
          if (isNaN(a.result.points) && !isNaN(b.result.points)) return 1
          if (!isNaN(a.result.points) && isNaN(b.result.points)) return -1
          return a.result.points - b.result.points
        }
      })
    },

    stageFilters: function () {
      let filters = []
      let newFilter = {}
      if (this.currentChampionship?.byClubs) {
        newFilter = {
          key: "club",
          title: this.$i18n.t("club"),
          flags: false,
        }
        let clubList = this.championshipCompetitors
          .map((competitor) => competitor.driver?.clubId)
          .concat(this.championshipCompetitors.map((competitor) => competitor.pilot?.clubId))
          .concat(this.championshipCompetitors.map((competitor) => competitor.pilot2?.clubId))
        let uniqClubList = [...new Set(clubList)].filter((club) => club !== null && club !== undefined)
        newFilter.list = [{ name: this.$i18n.t("everybody"), default: true }].concat(
          uniqClubList
            .map((clubId) => {
              return {
                value: clubId,
                name: this.competitionData.club.find((club) => club.id === clubId).shortName,
              }
            })
            .sort()
        )
        filters.push(newFilter)
      }
      if (this.currentChampionship?.byNationality) {
        newFilter = {
          key: "nationality",
          title: this.$i18n.t("nationality"),
          flags: true,
        }
        let nationalityList = this.championshipCompetitors
          .map((competitor) => competitor.driver?.nationality)
          .concat(this.championshipCompetitors.map((competitor) => competitor.pilot?.nationality))
          .concat(this.championshipCompetitors.map((competitor) => competitor.pilot2?.nationality))
        let uniqNationalityList = [...new Set(nationalityList)].filter((club) => club !== null && club !== undefined)
        newFilter.list = [{ name: this.$i18n.t("everybody"), default: true }].concat(
          uniqNationalityList
            .map((nationality) => {
              return { value: nationality, name: nationality }
            })
            .sort((a, b) => (a.name > b.name ? 1 : -1))
        )
        filters.push(newFilter)
      }
      if (this.currentChampionship?.byCategory) {
        newFilter = {
          key: "category",
          title: this.$i18n.t("category"),
          flags: false,
          list: [
            {
              name: this.$i18n.t("everybody"),
              default: true,
            },
          ].concat(this.getListToFilter("category", "categoryId")),
        }
        filters.push(newFilter)
      }
      if (this.currentChampionship?.byGroup) {
        newFilter = {
          key: "group",
          title: this.$i18n.t("group"),
          flags: false,
          list: [{ name: this.$i18n.t("everybody"), default: true }].concat(this.getListToFilter("group", "groupId")),
        }
        filters.push(newFilter)
      }
      if (this.currentChampionship?.byClass) {
        newFilter = {
          key: "class",
          title: this.$i18n.t("class"),
          flags: false,
          list: [{ name: this.$i18n.t("everybody"), default: true }].concat(this.getListToFilter("class", "classId")),
        }
        filters.push(newFilter)
      }
      if (this.currentChampionship?.bySpecial) {
        newFilter = {
          key: "specialClassification",
          title: this.$i18n.t("specialClassification"),
          flags: false,
        }
        let specialIdList = this.championshipCompetitors.reduce(
          (acc, competitor) => acc.concat(competitor.championshipParticipation[this.currentChampionship.id].specialIds),
          []
        )
        let uniqSpecialList = [...new Set(specialIdList)]
          .filter((club) => club !== null && club !== undefined)
          .map((specialId) =>
            this.competitionData.specialClassification.find(
              (specialClassification) => specialClassification.id === specialId
            )
          )
        newFilter.list = [
          {
            name: this.$i18n.t("everybody"),
            default: true,
          },
        ].concat(uniqSpecialList.sort((a, b) => (a.name > b.name ? 1 : -1)))
        filters.push(newFilter)
      }

      return filters
    },

    heroTitle: function () {
      return this.competition.dayNo
        ? `${this.competition.name} – ${this.$i18n.t("day")} ${this.competition.dayNo}`
        : this.competition.name
    },

    heroSubtitle: function () {
      const competitionDate = new Date(this.competition.competitionDate)
      let addString = ""
      if (this.competition.days > 1) {
        addString = competitionDate.getDate()
        const startDate = new Date(this.competition.competitionDate)
        competitionDate.setDate(competitionDate.getDate() + this.competition.days - 1)
        if (startDate.getMonth() !== competitionDate.getMonth()) {
          addString = this.$i18n
            .d(startDate)
            .replace("." + startDate.getFullYear(), "")
            .replace(startDate.getFullYear() + ".", "")
            .replace("/" + startDate.getFullYear(), "")
            .replace(startDate.getFullYear() + "/", "")
          addString
        }
        addString += "-"
      }
      return this.competition.cityName + ", " + addString + this.$i18n.d(competitionDate, "long")
    },

    allPenalties: function () {
      function preparePenaltyObject(competitors, penalty, timeObject, isOnStage) {
        return {
          ...penalty,
          stageNo: timeObject.stageNo,
          competitor: competitors.find((competitor) => competitor.id === timeObject.competitorId),
          isOnStage: isOnStage,
        }
      }

      return this.competitionData.time
        .map((time) =>
          time.penalties.in
            .map((penalty) => preparePenaltyObject(this.competitionData.competitor, penalty, time, true))
            .concat(
              time.penalties.out.map((penalty) =>
                preparePenaltyObject(this.competitionData.competitor, penalty, time, false)
              )
            )
        )
        .reduce((acc, penalties) => acc.concat(penalties), [])
        .sort((a, b) => a.stageNo - b.stageNo)
    },
    regularityData: function () {
      if (!this.currentStage) return null
      if ([4, 5].includes(this.currentStage.type)) {
        return this.currentStage.data.split(";").map((obj) => parseInt(obj))
      }
      return null
    },
  },

  watch: {
    competitionData: {
      deep: true,
      handler() {},
    },

    currentChampionship: {
      deep: true,
      handler() {},
    },
    lastTime: {
      deep: true,
      handler() {
        if (!this.lastTime) return
        if (this.lastTimeTimer) clearTimeout(this.lastTimeTimer)
        const self = this
        this.lastTimeTimer = setTimeout(function () {
          self.lastTime = null
        }, 10000)
      },
    },
  },

  created() {
    if (this.isDisplay) this.initDisplay()
    else this.initCompetition()
  },

  mounted() {
    if (this.isDisplay) {
      document.addEventListener("mousemove", this.onMouseMove)
      this.resetInactivityTimer()
    }
  },

  beforeDestroy() {
    if (this.isDisplay) {
      document.removeEventListener("mousemove", this.onMouseMove)
      this.clearInactivityTimers()
    }
    if (this.ws) this.ws.close()
    this.$emit("ws-change", false)
    this.$emit("show-penalties", false)
  },

  methods: {
    initCompetition() {
      const competitionName = this.$route.params.competition
      const endPointName = this.$route.meta.archival ? "archivalView" : "globalView"

      fetch(`${config.ManagerServer.rest.url}/${endPointName}/${competitionName}`)
        .then((response) => {
          if (response.ok) return response.json()
          throw new Error("Competition not found")
        })
        .then((data) => {
          // Język zawodów to tylko domyślny — ręczny wybór użytkownika ma pierwszeństwo.
          if (data.language && !hasSavedLocale()) this.$i18n.locale = data.language
          this.loadPartners(data.competitionId)
          this.connectWebSocket(data.competitionId)
        })
        .catch(() => (this.isNotFound = true))
    },

    async initDisplay() {
      const displayId = parseInt(this.$route.params.id)
      const displayConfig = await this.fetchDisplayConfig(displayId)
      if (!displayConfig) {
        // Pierwsze ładowanie bez internetu — nie pokazuj 404, zostań na ładowaniu i próbuj dalej.
        this.scheduleConfigRetry()
        return
      }
      this.startDisplay(displayConfig)
    },

    startDisplay(displayConfig) {
      this.displayConfig = displayConfig
      this.loadPartners(displayConfig.competitionId)
      this.connectWebSocket(displayConfig.competitionId)
    },

    scheduleConfigRetry() {
      if (this.configRetryTimer) clearTimeout(this.configRetryTimer)
      this.configRetryTimer = setTimeout(() => this.initDisplay(), 15000)
    },

    async fetchDisplayConfig(displayId) {
      try {
        const url = `${config.ManagerServer.rest.url}/displayConfiguration/${displayId}`
        const response = await fetch(url, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        })
        if (!response.ok) throw new Error(`HTTP error: ${response.status}`)
        return JSON.parse(await response.text())
      } catch (error) {
        console.log(error)
        return null
      }
    },

    loadPartners(competitionId) {
      fetch(`${config.ManagerServer.rest.url}/partnerLogotypes/${competitionId}`)
        .then((response) => response.json())
        .then((data) => (this.partners = data))
        .catch(() => {})
    },

    connectWebSocket(competitionId) {
      fetch(`${config.ManagerServer.rest.url}/websocket/${competitionId}`)
        .then((response) => response.json())
        .then((data) => {
          if (data.type !== "ok") return

          this.ws = new WebSocket(`wss://wyniki-online.pl:${data.port}`)

          this.ws.onmessage = (message) => {
            const msg = JSON.parse(message.data)

            if (msg.answer === "ok") {
              this.isUpdating = false
            } else if (msg.answer === "refresh") {
              window.location.reload()
            } else if (msg.type === "init" || msg.type === "archival") {
              this.status = "ready"
              this.currentHash = msg.hash
              this.competitionData = { ...msg.data }
              this.selectInitialStage()

              if (this.competitionData.competition[0].penalties) this.showPenaltiesBtn()
            } else if (this.currentHash === msg.oldHash) {
              const table = this.getTableName(msg)

              this.currentHash = msg.hash

              switch (msg.type) {
                case "insert":
                  table.push(msg.data)
                  break
                case "update":
                  table.splice(this.getTableElementIndex(msg), 1, msg.data)
                  break
                case "delete":
                  table.splice(this.getTableElementIndex(msg), 1)
                  break
              }
              if (msg.tableName === "time") this.lastTime = msg.data

              if (this.isUpdating) {
                this.ws.send(
                  JSON.stringify({
                    type: "reanswer",
                    hash: this.currentHash,
                  })
                )
              }
            } else {
              this.isUpdating = true

              this.ws.send(JSON.stringify({ type: "reanswer", hash: this.currentHash }))
            }
          }

          this.ws.onopen = () => {
            this.ws.send(`{ "type": "init", "id": "${competitionId}" }`)
            this.$emit("ws-change", true)
          }

          this.ws.onerror = () => {
            this.$emit("ws-change", false)
            if (!this.isDisplay) this.$emit("modal-status", "lostConnectionModal")
          }

          this.ws.onclose = () => {
            this.$emit("ws-change", false)
            if (!this.isDisplay) this.$emit("modal-status", "lostConnectionModal")
          }
        })
        .catch(() => (this.isNotFound = true))
    },

    selectInitialStage() {
      if (this.isDisplay) {
        // Start od pierwszego championship, preferowany stage = ten z konfiguracji.
        // Dla display dodatkowo szukamy championship, który faktycznie ma wyniki.
        this.selectDisplayChampionship(this.competitionData.championship[0], this.displayConfig.stageNo)
      } else {
        this.currentChampionship = this.competitionData.championship[0]
        if (this.competitionData.competition[0].mode === 0)
          this.currentStage = this.competitionData.stage.find((stage) => stage.stageNo === this.recentMessage.stageNo)
        else this.currentStage = this.competitionData.stage[this.competitionData.stage.length - 1]
        if (!this.currentStage) this.currentStage = this.competitionData.stage[0]
      }
    },

    // Ustawia championship oraz stage: próbuje trafić w ten sam stageNo. Jak takiego odcinka tu nie ma,
    // to żaden nie jest wybrany (currentStage = null) i tabela się po prostu nie ładuje.
    applyChampionship(championship, preferredStageNo) {
      this.currentChampionship = championship
      if (preferredStageNo == null) {
        this.currentStage = null
        return
      }
      this.currentStage = this.championshipStages.find((stage) => stage.stageNo === preferredStageNo) || null
    },

    onChampionshipClick(championship) {
      const preferredStageNo = this.currentStage ? this.currentStage.stageNo : null
      if (this.isDisplay) this.selectDisplayChampionship(championship, preferredStageNo)
      else this.applyChampionship(championship, preferredStageNo)
    },

    // Display: od wskazanego championship szukamy (po kolei, z zawinięciem) pierwszego, który ma wyniki.
    // Jak żaden nie ma — zostawiamy pierwszy championship.
    selectDisplayChampionship(startChampionship, preferredStageNo) {
      const championships = this.competitionData.championship
      const startIndex = Math.max(0, championships.indexOf(startChampionship))

      for (let i = 0; i < championships.length; i++) {
        const championship = championships[(startIndex + i) % championships.length]
        this.applyChampionship(championship, preferredStageNo)
        if (this.currentStage && this.stageResults.length > 0) return
      }

      // Nigdzie nie ma wyników — zostaw pierwszy championship. Na ekranie display nie chcemy pustki,
      // więc jak preferowanego odcinka tam nie ma, bierzemy pierwszy dostępny.
      this.applyChampionship(championships[0], preferredStageNo)
      if (!this.currentStage) this.currentStage = this.championshipStages[0] || null
    },

    async resetDisplay() {
      const displayId = parseInt(this.$route.params.id)
      const displayConfig = await this.fetchDisplayConfig(displayId)

      // Modal bezczynności chowamy zawsze.
      this.showInactivityModal = false
      this.stopCountdown()

      if (!displayConfig) {
        // Nie udało się odświeżyć (np. brak internetu) — nic nie ruszamy, zostajemy jak byliśmy.
        this.resetInactivityTimer()
        return
      }

      // Sukces — burzymy obecny widok i budujemy od nowa.
      if (this.ws) {
        this.ws.close()
        this.ws = null
      }

      this.status = "loading"
      this.competitionData = null
      this.isNotFound = false
      this.partners = null

      this.startDisplay(displayConfig)
      this.resetInactivityTimer()
    },

    resetInactivityTimer() {
      if (this.inactivityTimer) clearTimeout(this.inactivityTimer)
      this.inactivityTimer = setTimeout(() => {
        this.countdown = 15
        this.showInactivityModal = true
        this.startCountdown()
      }, 5 * 60 * 1000)
    },

    onMouseMove() {
      if (this.showInactivityModal) {
        this.showInactivityModal = false
        this.stopCountdown()
      }
      this.resetInactivityTimer()
    },

    startCountdown() {
      this.countdownTimer = setInterval(() => {
        this.countdown--
        if (this.countdown <= 0) this.resetDisplay()
      }, 1000)
    },

    stopCountdown() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer)
        this.countdownTimer = null
      }
    },

    clearInactivityTimers() {
      if (this.inactivityTimer) clearTimeout(this.inactivityTimer)
      if (this.configRetryTimer) clearTimeout(this.configRetryTimer)
      this.stopCountdown()
    },

    cutTimeToPrecision(time, precision) {
      for (let i = 0; i < 3 - precision; i++) time /= 10
      time = Math.floor(time)
      for (let i = 0; i < 3 - precision; i++) time *= 10
      return time
    },
    showPenaltiesBtn() {
      this.$emit("show-penalties", true)
    },

    getTableName(msg) {
      return this.competitionData[msg.tableName]
    },

    getTableElementIndex(msg) {
      if (msg.tableName === "time") {
        return this.getTableName(msg).findIndex(
          (el) => el.competitorId === msg.data.competitorId && el.stageNo === msg.data.stageNo
        )
      }

      return this.getTableName(msg).findIndex((el) => el.id === msg.data.id)
    },

    getAvgSpeed: function (ms, length) {
      if (this.currentStage.type === 2 || this.currentStage.type === 3) ms = ms?.realTime
      if (this.currentStage.type === 4 || this.currentStage.type === 5) ms = ms?.time
      const km = length / 1000
      const h = ms / 3600000
      return Math.round(km / h)
    },

    getCompetitorTime: function (competitorId) {
      if (this.currentStage === null) return null

      switch (this.currentStage.type) {
        case 0:
        case 6:
          if (this.afterStage)
            return this.getCompetitorTimeAfterStage(competitorId, this.currentStage.stageNo, this.currentStage.type)
          return this.getCompetitorTimeSingleStage(competitorId, this.currentStage.stageNo)
        case 1:
        case 7:
          return this.getCompetitorTimeGroupedStages(competitorId, this.currentStage.group, this.currentStage.type - 1)
        case 2:
          return this.getCompetitorMinTimeSingleStage(competitorId, this.currentStage.stageNo, this.currentStage.data)
        case 3:
          return this.getCompetitorMinTimeGroupedStages(competitorId, this.currentStage.group, this.currentStage.data)
        case 4:
          return this.getCompetitorTimeRegularity(competitorId, this.currentStage)
        case 5:
          return this.getCompetitorTimeRegularitySum(competitorId, this.currentStage)
      }
    },

    getCompetitorSplitTimes: function (competitorId, currentStage) {
      if (currentStage === null) return null

      switch (currentStage.type) {
        case 0:
          return this.competitionData.time.find(
            (time) => time.stageNo === currentStage.stageNo && time.competitorId === competitorId
          )?.splitTimes
        case 1:
        case 2:
        case 3:
        case 5:
        case 6:
        case 7:
          return []
        case 4:
          return this.competitionData.time
            .find((time) => time.stageNo === currentStage.stageNo && time.competitorId === competitorId)
            ?.splitTimes.map((splitTime, index) =>
              this.regularityData[index] && splitTime
                ? {
                    time: splitTime,
                    speed: this.getAvgSpeed({ time: splitTime }, this.regularityData[index]),
                    maxSpeed: this.getPerfectSpeed(competitorId) + 10,
                    points: this.calculatePoints(
                      splitTime,
                      this.regularityData[index],
                      this.getPerfectSpeed(competitorId)
                    ),
                  }
                : null
            )
      }
    },
    calculatePoints: function (time, length, speed) {
      return Math.round(Math.abs(time - Math.round((length / speed) * 3600)))
    },
    getPerfectSpeed: function (competitorId) {
      const champParticipation = this.competitionData.competitor.find((competitor) => competitor.id === competitorId)
        .championshipParticipation
      return parseInt(champParticipation[this.currentChampionship.id].data)
    },
    getStageLength: function () {
      if (!this.currentStage) return null

      let multiplier = 1
      switch (this.currentStage.type) {
        case 0:
        case 2:
        case 6:
          if (this.afterStage && this.currentStage)
            return this.getGroupedStages(this.currentStage.group, this.currentStage.type)
              .filter((stage) => stage.stageNo <= this.currentStage?.stageNo)
              .reduce((acc, stage) => acc + stage.length, 0)
          return (
            this.competitionData.stage.find(
              (stage) => stage.stageNo === this.currentStage?.stageNo && stage.group === this.currentStage?.group
            ).length * multiplier
          )
        case 1:
        case 7:
          return this.getGroupedStages(this.currentStage.group, this.currentStage.type - 1).reduce(
            (acc, stage) => acc + stage.length,
            0
          )
        case 3:
          return this.getGroupedStages(this.currentStage.group, 2).reduce(
            (acc, stage) => Math.min(acc, stage.length),
            9999999999
          )
        case 4:
          return this.currentStage.length
        case 5:
          return this.getGroupedStages(this.currentStage.group, 4).reduce((acc, stage) => acc + stage.length, 0)
      }
    },

    getCompetitorTimeSingleStage: function (competitorId, stageNo) {
      if (stageNo) {
        const competitorTime = this.competitionData.time.find(
          (time) => time.stageNo === stageNo && time.competitorId === competitorId
        )
        if (competitorTime.atypicalResultId) {
          const atypicalResult = this.competitionData.atypicalResult.find(
            (result) => result.id === competitorTime.atypicalResultId
          )

          return atypicalResult?.shortName
        }

        return this.cutTimeToPrecision(competitorTime.time, this.competition.precision)
      }

      return null
    },
    getCompetitorTimeAfterStage: function (competitorId, stageNo, typeToGroup) {
      if (stageNo) {
        const stageNoList = this.getGroupedStages(this.currentStage.group, typeToGroup).map((stage) => stage.stageNo)
        const competitorTimes = this.competitionData.time
          .filter(
            (time) =>
              stageNoList.includes(time.stageNo) && time.stageNo <= stageNo && time.competitorId === competitorId
          )
          .sort((a, b) => b.stageNo - a.stageNo)
        let time = 0
        for (let competitorTime of competitorTimes) {
          if (competitorTime.atypicalResultId) {
            const atypicalResult = this.competitionData.atypicalResult.find(
              (result) => result.atypicalResultId === competitorTime.atypicalResultId
            )

            return atypicalResult?.shortName
          }
          if (competitorTime.time === null) return null
          time += this.cutTimeToPrecision(competitorTime.time, this.competition.precision)
        }
        return time
      }

      return null
    },

    getCompetitorMinTimeSingleStage: function (competitorId, stageNo, targetTime) {
      if (stageNo) {
        const competitorTime = this.competitionData.time.find(
          (time) => time.stageNo === stageNo && time.competitorId === competitorId
        )
        if (competitorTime.atypicalResultId) {
          const atypicalResult = this.competitionData.atypicalResult.find(
            (result) => result.atypicalResultId === competitorTime.atypicalResultId
          )

          return atypicalResult?.shortName
        }
        if (competitorTime.time === null) return null
        return {
          time: Math.abs(targetTime - this.cutTimeToPrecision(competitorTime.time, this.competition.precision)),
          realTime: competitorTime.time,
        }
      }

      return null
    },

    getGroupedStages: function (group, type) {
      return this.competitionData.stage.filter(
        (stage) =>
          stage.group === group && stage.type === type && stage.championshipIds.includes(this.currentChampionship.id)
      )
    },

    getCompetitorTimeGroupedStages: function (competitorId, group, typeToGroup) {
      const groupedStages = this.getGroupedStages(group, typeToGroup)
      const stageNoList = groupedStages.map((stage) => stage.stageNo)
      const competitorGroupedTimes = this.competitionData.time.filter(
        (time) =>
          stageNoList.includes(time.stageNo) &&
          time.competitorId === competitorId &&
          time.time !== null &&
          time.atypicalResultId === null
      )
      if (competitorGroupedTimes.length !== groupedStages.length) return null
      return competitorGroupedTimes.reduce(
        (acc, time) => acc + this.cutTimeToPrecision(time.time, this.competition.precision),
        0
      )
    },
    getCompetitorTimeGroupedStagesMinimum: function (competitorId, group) {
      const groupedStages = this.getGroupedStages(group, 0)
      const stageNoList = groupedStages.map((stage) => stage.stageNo)
      const competitorGroupedTimes = this.competitionData.time.filter(
        (time) =>
          stageNoList.includes(time.stageNo) &&
          time.competitorId === competitorId &&
          time.time !== null &&
          time.atypicalResultId === null
      )
      if (competitorGroupedTimes.length === 0) return null
      return competitorGroupedTimes
        .filter((time) => time.time > 0)
        .reduce((acc, time) => Math.min(acc, time.time), 999999999)
    },

    getCompetitorMinTimeGroupedStages: function (competitorId, group, targetTime) {
      const groupedStages = this.getGroupedStages(group, 2)
      const stageNoList = groupedStages.map((stage) => stage.stageNo)
      const competitorGroupedTimes = this.competitionData.time.filter(
        (time) =>
          stageNoList.includes(time.stageNo) &&
          time.competitorId === competitorId &&
          time.time !== null &&
          time.atypicalResultId === null
      )

      if (competitorGroupedTimes.length !== groupedStages.length) return null

      let sumTime = 0
      for (let time of competitorGroupedTimes) sumTime += time.time
      const avgTime = Math.floor(sumTime / competitorGroupedTimes.length)
      return { time: Math.abs(avgTime - targetTime), realTime: avgTime }
    },
    getCompetitorTimeRegularity: function (competitorId, currentStage) {
      const metaTime = this.getCompetitorTimeSingleStage(competitorId, currentStage.stageNo)
      if (isNaN(metaTime) || metaTime <= 0) return metaTime
      const splitTimes = this.getCompetitorSplitTimes(competitorId, currentStage)
      let sumPoints = 0
      for (let i in splitTimes) {
        if (!this.regularityData[i]) continue
        else if (splitTimes[i]) sumPoints += splitTimes[i].points
        else return null
      }
      const perfectSpeed = this.getPerfectSpeed(competitorId)
      const metaPoints = this.calculatePoints(metaTime, currentStage.length, perfectSpeed)
      sumPoints += metaPoints
      return {
        points: sumPoints,
        time: metaTime,
        meta: {
          time: metaTime,
          speed: this.getAvgSpeed({ time: metaTime }, currentStage.length),
          maxSpeed: perfectSpeed + 10,
          points: metaPoints,
        },
      }
    },
    getCompetitorTimeRegularitySum: function (competitorId, currentStage) {
      const groupedStages = this.getGroupedStages(currentStage.group, 4)
      const result = { points: 0, time: 0 }
      for (const stage of groupedStages) {
        const stageResult = this.getCompetitorTimeRegularity(competitorId, stage)
        if (!stageResult) return null
        result.points += stageResult.points
        result.time += stageResult.time
      }
      return result
    },

    // Wyniki końcowe (typ 1 i 7) sumują cały przebieg — tam do czasu liczą się wszystkie kary.
    isFinalStage: function (stage) {
      return stage.type === 1 || stage.type === 7
    },

    sumPenalties: function (penalties) {
      return penalties.reduce((acc, penalty) => acc + penalty.seconds, 0) * 1000
    },

    // Kary rozbite na sportowe (in) i pozostałe (out), w milisekundach.
    // null oznacza wynik nietypowy — tak jak wcześniej nie liczymy dla niego kar.
    getCompetitorPenalties: function (competitorId, stage) {
      if (this.afterStage || this.isFinalStage(stage)) {
        let competitorTimes = null
        const stageNoList = this.getGroupedStages(stage.group, stage.type % 2 === 0 ? stage.type : stage.type - 1).map(
          (stage) => stage.stageNo
        )
        if (this.isFinalStage(stage)) {
          competitorTimes = this.competitionData.time.filter(
            (time) => stageNoList.includes(time.stageNo) && time.competitorId === competitorId
          )
        } else {
          competitorTimes = this.competitionData.time.filter(
            (time) =>
              stageNoList.includes(time.stageNo) && time.stageNo <= stage.stageNo && time.competitorId === competitorId
          )
        }
        let penalties = { in: 0, out: 0 }
        for (let competitorTime of competitorTimes) {
          if (competitorTime.atypicalResultId) return null
          penalties.in += this.sumPenalties(competitorTime.penalties.in)
          penalties.out += this.sumPenalties(competitorTime.penalties.out)
        }
        return penalties
      } else if (stage.type === 4) {
        return { in: 0, out: 0 }
      } else {
        const competitorTime = this.competitionData.time.find(
          (time) => time.stageNo === stage.stageNo && time.competitorId === competitorId
        )
        if (competitorTime.atypicalResultId) return null

        return {
          in: this.sumPenalties(competitorTime.penalties.in),
          out: this.sumPenalties(competitorTime.penalties.out),
        }
      }
    },

    // Kara doliczana do czasu i brana pod uwagę przy sortowaniu: tylko sportowe (in).
    // Wyjątek to wyniki końcowe, gdzie do klasyfikacji wchodzą również kary "out".
    getEffectivePenalty: function (penalties, stage) {
      if (!penalties) return null
      return this.isFinalStage(stage) ? penalties.in + penalties.out : penalties.in
    },

    getListToFilter: function (valueName, valueId) {
      const idList = this.championshipCompetitors.map(
        (competitor) => competitor.championshipParticipation[this.currentChampionship.id][valueId]
      )

      const uniqNameList = [...new Set(idList)]
        .filter((el) => el !== null && el !== undefined)
        .map((mapId) => this.competitionData[valueName].find((el) => el.id === mapId))

      return uniqNameList.sort((a, b) => (a.name > b.name ? 1 : -1))
    },
  },
}
</script>

<style scoped lang="scss">
.competition-nav {
  background: $primary;
  box-shadow: 0 0 1rem rgba(#000, 0.35);

  .stages {
    border-top: 1px solid rgba(#fff, 0.15);

    @media (max-width: 650px) {
      display: grid;
      grid-template-columns: 1fr 1fr;
    }
  }

  ul {
    display: flex;
    flex-wrap: wrap;
    max-width: $containerWidth;
    padding: 0.25rem;
    margin: auto;

    li {
      display: flex;
    }

    button {
      color: #fff;
      font-size: 0.85rem;
      font-weight: bold;
      border: 2px solid #fff;
      border-radius: 0.25rem;
      padding: 0.25rem 1rem;
      margin: 0.5rem;
      display: block;
      text-transform: uppercase;
      transition: color 250ms, background 250ms;
      width: 100%;

      display: flex;
      justify-content: center;
      flex-direction: column;

      .meta {
        font-size: 0.65rem;
        font-weight: normal;
      }

      &.active {
        color: #4f4f4f;
        background: #fff;
        box-shadow: 0 0 1rem rgba(#000, 0.25);
      }

      &.disabled {
        pointer-events: none;
        opacity: 0.75;
      }
    }
  }
}

.content {
  margin: auto;
  max-width: $containerWidth;
}

.inactivity-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.82);
}

.inactivity-box {
  background: #fff;
  border-radius: 1rem;
  padding: 3rem 4rem;
  text-align: center;
  max-width: 480px;
  width: 90%;
  box-shadow: 0 8px 48px rgba(0, 0, 0, 0.4);

  &__title {
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0 0 1.5rem;
  }

  &__countdown {
    font-size: 6rem;
    font-weight: 900;
    line-height: 1;
    color: #c0392b;
    margin-bottom: 1rem;
  }

  &__subtitle {
    font-size: 1rem;
    color: #444;
    margin: 0 0 1.5rem;
  }

  &__progress {
    height: 6px;
    background: #eee;
    border-radius: 3px;
    overflow: hidden;
    margin-bottom: 1.5rem;

    &-bar {
      height: 100%;
      background: #c0392b;
      border-radius: 3px;
      transition: width 1s linear;
    }
  }

  &__hint {
    font-size: 0.85rem;
    color: #999;
    margin: 0;
  }
}

.inactivity-fade-enter-active,
.inactivity-fade-leave-active {
  transition: opacity 300ms ease;
}

.inactivity-fade-enter,
.inactivity-fade-leave-to {
  opacity: 0;
}
</style>
